import { ArrowRight, Boxes, Check, MoveRight, Quote, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { services } from "../site-data";
import { Seo } from "../components/seo";
import { Badge, ButtonLink, Callout, Section, SectionHeading } from "../components/ui";

export default function Home() {
  const serviceAreas = ["Hilversum", "Blaricum", "Naarden", "Huizen", "Almere"];
  return (
    <>
      <Seo title="Data, automatisering & IoT in het Gooi en Almere | STAQL" description="STAQL helpt MKB in Hilversum, Blaricum, Naarden, Huizen en Almere met data, AI, automatisering, websoftware en IoT." />
      <section className="home-hero">
        <div className="container home-hero__grid">
          <div className="home-hero__copy">
            <Badge><Sparkles aria-hidden="true" size={14} /> Data · Automatisering · IoT</Badge>
            <h1>Maak van digitaal gedoe een <em>werkend systeem.</em></h1>
            <p>Digitaal gedoe hoeft je bedrijf niet te remmen. STAQL maakt er een werkend systeem van, zodat kleine bedrijven en het MKB beter kunnen sturen, handwerk verminderen en digitale ideeën waarmaken. Van dashboards en AI tot automatisering, websoftware en slimme apparaten.</p>
            <div className="home-hero__actions">
              <ButtonLink to="/contact" showArrow>Plan een kennismaking</ButtonLink>
              <ButtonLink to="/diensten" variant="secondary">Bekijk onze diensten</ButtonLink>
            </div>
            <div className="hero-trust" aria-label="Sterke punten">
              <span><Check aria-hidden="true" />Lokaal in Huizen</span>
              <span><Check aria-hidden="true" />Eén vast aanspreekpunt</span>
              <span><Check aria-hidden="true" />Specialisten wanneer nodig</span>
            </div>
          </div>
          <div className="hero-media">
            <div className="hero-media__frame">
              <video autoPlay muted loop playsInline preload="metadata" width="960" height="540" poster="/media/staql-boxes-poster.jpg" aria-label="STAQL-dozen bewegen over een productielijn">
                <source src="/media/staql-boxes.webm" type="video/webm" />
                <source src="/media/staql-boxes.mp4" type="video/mp4" />
              </video>
              <div className="hero-media__shade" />
              <div className="hero-media__label"><span className="signal-dot" />Van idee naar uitvoering</div>
            </div>
            <div className="hero-floating-card">
              <span>Technologie</span>
              <strong>die vooruitwerkt</strong>
              <MoveRight aria-hidden="true" />
            </div>
          </div>
        </div>
      </section>

      <Section className="section--intro">
        <div className="split-heading">
          <span className="eyebrow">De kern</span>
          <div>
            <h2>Techniek is pas waardevol als je bedrijf er beter van werkt.</h2>
            <p>Misschien werk je met losse spreadsheets, kost een terugkerend proces te veel tijd of heb je een idee waarvoor techniek nodig is. STAQL brengt structuur aan, maakt de mogelijkheden concreet en bouwt wat nodig is.</p>
          </div>
        </div>
        <div className="value-strip">
          {[
            ["01", "Minder handwerk", "Automatiseer terugkerende taken en voorkom dubbel invoeren."],
            ["02", "Meer inzicht", "Maak data begrijpelijk en bruikbaar voor dagelijkse beslissingen."],
            ["03", "Ruimte om te groeien", "Bouw een digitale basis die met je organisatie kan meegroeien."]
          ].map(([number, title, text]) => <article key={number}><span>{number}</span><h3>{title}</h3><p>{text}</p></article>)}
        </div>
      </Section>

      <Section className="section--tint">
        <SectionHeading eyebrow="Diensten" title="Drie heldere ingangen. Eén samenhangende oplossing." text="Geen lange menukaart met technische termen. Wel drie gebieden waarin we een probleem van analyse tot werkende oplossing brengen." />
        <div className="service-grid">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <article className="service-card" key={service.slug}>
                <div className="service-card__top"><span className="service-card__index">0{index + 1}</span><span className="service-card__icon"><Icon aria-hidden="true" /></span></div>
                <h3>{service.cardTitle}</h3>
                <p>{service.description}</p>
                <Link className="service-card__link" to={`/diensten/${service.slug}`}>Ontdek {service.shortTitle}<ArrowRight aria-hidden="true" /></Link>
              </article>
            );
          })}
        </div>
      </Section>

      <Section>
        <div className="process-grid">
          <div className="process-copy">
            <span className="eyebrow">Werkwijze</span>
            <h2>Van vraagstuk naar iets dat werkt.</h2>
            <p>We beginnen niet bij een tool, maar bij wat je wilt verbeteren. Zo blijft de oplossing begrijpelijk, beheersbaar en in verhouding tot het probleem.</p>
            <ButtonLink to="/over-staql" variant="secondary" showArrow>Zo werkt STAQL</ButtonLink>
          </div>
          <ol className="process-list">
            {[
              ["Vraag scherpstellen", "Wat loopt vast, wie merkt dat en wat moet er aantoonbaar beter?"],
              ["Slimste route kiezen", "Wat is nodig, wat is er al en waar voegt maatwerk werkelijk iets toe?"],
              ["Bouwen en koppelen", "STAQL realiseert de oplossing en betrekt waar nodig vertrouwde specialisten."],
              ["Werkend houden", "Duidelijk opleveren, kennis overdragen en afspraken maken over doorontwikkeling."]
            ].map(([title, text], index) => <li key={title}><span>0{index + 1}</span><div><h3>{title}</h3><p>{text}</p></div></li>)}
          </ol>
        </div>
      </Section>

      <Section className="section--case">
        <div className="case-feature">
          <div className="case-feature__visual">
            <div className="data-orbit" aria-hidden="true"><span /><span /><span /><span /></div>
            <div className="case-stat"><strong>Volledig</strong><span>inzicht in beschikbare afvaldata</span></div>
          </div>
          <div className="case-feature__copy">
            <span className="eyebrow">Case · Schenk Recycling</span>
            <h2>Afvaldata vertaald naar inzicht voor klanten.</h2>
            <p>STAQL ontwikkelde een totaaloplossing met klantgerichte dashboards. Klanten gingen van geen centraal inzicht naar volledig overzicht van hun beschikbare afvalgegevens.</p>
            <div className="case-tags"><Badge>Data</Badge><Badge>Dashboards</Badge><Badge>CSRD-ondersteunend</Badge></div>
            <ButtonLink to="/cases/schenk-recycling" variant="secondary" showArrow>Bekijk de case</ButtonLink>
          </div>
        </div>
      </Section>

      <Section>
        <SectionHeading eyebrow="Waarom STAQL" title="Technisch sterk. Praktisch in de samenwerking." align="center" />
        <div className="reason-grid">
          {[
            ["Eén aanspreekpunt", "Je schakelt direct met Youssef: van eerste analyse tot oplevering."],
            ["Nuchter advies", "Niet elke uitdaging vraagt om maatwerk of AI. Je krijgt een voorstel dat past."],
            ["Brede slagkracht", "Kennis van data, software, automatisering en IoT, aangevuld waar nodig."],
            ["Gebouwd voor de praktijk", "Een oplossing is pas geslaagd als mensen ermee kunnen werken."]
          ].map(([title, text]) => <article key={title}><span><Boxes aria-hidden="true" /></span><h3>{title}</h3><p>{text}</p></article>)}
        </div>
      </Section>

      <Section className="section--service-area">
        <div className="service-area">
          <div><span className="eyebrow">Werkgebied</span><h2>Dichtbij in het Gooi en Almere.</h2></div>
          <div><p>STAQL werkt vanuit Huizen voor ondernemers in Hilversum, Blaricum, Naarden, Huizen, Almere en de rest van het Gooi. Je werkt met een lokale technische partner die de lijnen kort houdt en waar nodig op locatie kan aansluiten.</p><ul aria-label="Plaatsen in het werkgebied">{serviceAreas.map((place) => <li key={place}>{place}</li>)}</ul></div>
        </div>
      </Section>

      <Section className="testimonial-section">
        <div className="testimonial">
          <Quote aria-hidden="true" />
          <blockquote>“Voorheen hadden onze klanten geen centraal inzicht in hun afvalgegevens. Met de dashboards van STAQL staat de relevante informatie overzichtelijk bij elkaar en kunnen klanten die direct gebruiken voor inzicht, verbetering en duurzaamheidsrapportage.”</blockquote>
          <cite>Daan · Schenk Recycling</cite>
        </div>
      </Section>

      <Callout title="Waar kan technologie voor jou beter werken?" text="Vertel waar je bedrijf tijd verliest, inzicht mist of een digitaal idee wil realiseren. In een eerste gesprek brengen we de mogelijkheden terug tot een concrete volgende stap." />
    </>
  );
}
